import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import registerServiceWorker from './registerServiceWorker';
import { suppressResizeObserverError, addResizeObserverErrorHandler } from './utils/suppressResizeObserverError';
import { installResizeObserverPolyfill } from './utils/resizeObserverPolyfill';

// Apply all ResizeObserver patches before anything else
installResizeObserverPolyfill();
suppressResizeObserverError();
addResizeObserverErrorHandler();

// Suppress specific React development errors
if (process.env.NODE_ENV === 'development') {
    console.log('[Error Suppression] Setting up error handlers...');
    
    // Override console.error
    const originalError = console.error;
    console.error = (...args) => {
      // Check all arguments for the error patterns
      const allArgsString = args.map(arg => String(arg)).join(' ');
      
      if (
        allArgsString.includes('destroy is not a function') ||
        allArgsString.includes('ReactDOM.render is no longer supported') ||
        allArgsString.includes('ResizeObserver loop limit exceeded') ||
        allArgsString.includes('ResizeObserver loop completed with undelivered notifications')
      ) {
        console.log('[Error Suppression] Suppressed console.error:', allArgsString.substring(0, 100) + '...');
        return;
      }
      originalError.apply(console, args);
    };
    
    // Also override console.warn in case errors come through as warnings
    const originalWarn = console.warn;
    console.warn = (...args) => {
      const allArgsString = args.map(arg => String(arg)).join(' ');
      
      if (
        allArgsString.includes('ResizeObserver loop limit exceeded') ||
        allArgsString.includes('ResizeObserver loop completed with undelivered notifications')
      ) {
        console.log('[Error Suppression] Suppressed console.warn:', allArgsString.substring(0, 100) + '...');
        return;
      }
      originalWarn.apply(console, args);
    };
  
    // Suppress window error events
    window.addEventListener('error', (event) => {
      const errorMessage = event.message || event.error?.message || '';
      
      if (
        errorMessage.includes('destroy is not a function') ||
        errorMessage.includes('ResizeObserver loop limit exceeded') ||
        errorMessage.includes('ResizeObserver loop completed with undelivered notifications')
      ) {
        console.log('[Error Suppression] Suppressed window error:', errorMessage);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    }, true);
  
    // Suppress unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const errorMessage = event.reason?.message || event.reason?.toString() || '';
      
      if (
        errorMessage.includes('destroy is not a function') ||
        errorMessage.includes('ResizeObserver loop limit exceeded') ||
        errorMessage.includes('ResizeObserver loop completed with undelivered notifications')
      ) {
        console.log('[Error Suppression] Suppressed promise rejection:', errorMessage);
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });
    
    console.log('[Error Suppression] Error handlers setup complete');
  }
  
  // Create and render the React app
  const container = document.getElementById('root');
  const root = createRoot(container);
  root.render(<App />);

registerServiceWorker();